import {
	Alert,
	Box,
	Button,
	Card,
	CardActionArea,
	CardContent,
	CircularProgress,
	Stack,
	Typography,
} from "@mui/material";
import { usePostSamplingIssuesViewModel } from "./PostSamplingIssues.vm";
import { JobDetailPane } from "./JobDetailPane";

const PANEL_HEIGHT = "calc(100vh - 300px)";

export const PostSamplingIssues: React.FC = () => {
	const vm = usePostSamplingIssuesViewModel();

	if (!vm.isDeveloper) {
		return (
			<Box sx={{ p: { xs: 2, sm: 4 }, maxWidth: "md", margin: "auto" }}>
				<Alert severity="error">You do not have permission to access this page.</Alert>
			</Box>
		);
	}

	return (
		<Box sx={{ p: { xs: 2, sm: 4 }, maxWidth: "xl", margin: "auto" }}>
			<Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
				<Typography variant="h5">Post-Sampling Issues</Typography>
				<Button
					variant="contained"
					onClick={vm.fixAll}
					disabled={vm.loading || vm.fixingAll || vm.jobs.length === 0}
					startIcon={vm.fixingAll ? <CircularProgress size={20} color="inherit" /> : undefined}
				>
					{vm.fixingAll
						? `Reprocessing All... ${vm.countdown !== null ? `(${vm.countdown}s)` : ""}`
						: `Reprocess All (${vm.jobs.length})`}
				</Button>
			</Stack>

			{vm.jobs.length > 0 && (
				<Alert severity="info" sx={{ mb: 2 }}>
					These jobs are stuck in post-sampling processing.
				</Alert>
			)}

			{vm.error && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{vm.error}
				</Alert>
			)}

			{vm.loading ? (
				<Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
					<CircularProgress />
				</Box>
			) : vm.jobs.length === 0 ? (
				<Alert severity="success">No post-sampling issues found.</Alert>
			) : (
				<Box sx={{ display: "flex", gap: 2 }}>
					{/* Left panel — compact job cards */}
					<Box
						sx={{
							width: 350,
							minWidth: 350,
							height: PANEL_HEIGHT,
							overflowY: "auto",
						}}
					>
						<Stack spacing={2}>
							{vm.groupedJobs.map(([company, jobs]) => (
								<Box key={company}>
									<Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ px: 1 }}>
										{company} ({jobs.length})
									</Typography>
									<Stack spacing={1} sx={{ mt: 0.5 }}>
										{jobs.map((job) => {
											const isSelected = vm.selectedJob?.id === job.id;
											return (
												<Card
													key={job.id}
													variant="outlined"
													sx={{
														borderColor: isSelected ? "primary.main" : undefined,
														borderWidth: isSelected ? 2 : 1,
														bgcolor: isSelected ? "action.selected" : undefined,
													}}
												>
													<CardActionArea onClick={() => vm.selectJob(job)}>
														<CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
															<Typography variant="body2" fontWeight="bold" noWrap>
																{job.fieldName || "Unnamed Field"}
															</Typography>
															<Typography variant="caption" color="text.secondary" noWrap component="div">
																{job.growerName || "—"}
															</Typography>
															<Typography variant="caption" color="text.secondary" noWrap component="div">
																{job.status || "—"}
															</Typography>
														</CardContent>
													</CardActionArea>
												</Card>
											);
										})}
									</Stack>
								</Box>
							))}
						</Stack>
					</Box>

					{/* Right panel — detail pane */}
					<Box
						sx={{
							flex: 1,
							height: PANEL_HEIGHT,
							overflowY: "auto",
						}}
					>
						{!vm.selectedJob ? (
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									height: "100%",
								}}
							>
								<Typography color="text.secondary">Select a job to view details</Typography>
							</Box>
						) : (
							<JobDetailPane vm={vm} />
						)}
					</Box>
				</Box>
			)}
		</Box>
	);
};
